import React, { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { NotificationsProvider } from "@/lib/notifications";
import { Toaster } from "@/components/ui/sonner";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Pending from "@/pages/Pending";
import VerifyEmail from "@/pages/VerifyEmail";
import ViewFile from "@/pages/ViewFile";

import Layout from "@/components/Layout";
import PublicLayout from "@/components/PublicLayout";
import Dashboard from "@/pages/Dashboard";
import Properties from "@/pages/Properties";
import Clients from "@/pages/Clients";
import PropertyDetail from "@/pages/PropertyDetail";
import Marketplace from "@/pages/Marketplace";
import PlafondSummary from "@/pages/PlafondSummary";
import AdminMarketplace from "@/pages/AdminMarketplace";
import KanbanBoard from "@/pages/KanbanBoard";
import Users from "@/pages/Users";
import CalendarPage from "@/pages/CalendarPage";
import ProCalendarPage from "@/pages/ProCalendarPage";
import SiteWizard from "@/pages/SiteWizard";
import SitePreview from "@/pages/SitePreview";
import Accounting from "@/pages/Accounting";
import Settings from "@/pages/Settings";
// Iter209ag — /my-domain è di nuovo una pagina wizard vera e propria per
// gestire la configurazione post-conferma piano (email/site/all).
import MyDomain from "@/pages/MyDomain";
// Iter209af — Client email (mock statico) + impostazioni email
import EmailPage from "@/pages/Email";
import EmailSettings from "@/pages/EmailSettings";
import JoinInvite from "@/pages/JoinInvite";
import ServicesAdmin from "@/pages/ServicesAdmin";
import PracticeDetail from "@/pages/PracticeDetail";
import MyCompensations from "@/pages/MyCompensations";
import TeamPage from "@/pages/TeamPage";
import ChatbotAdmin from "@/pages/ChatbotAdmin";
import ChatbotSettings from "@/pages/ChatbotSettings";
import ChatbotHub from "@/pages/ChatbotHub";
// Iter207.26 — Rimossi import ChatHistory + rotta /chat-history + pagina.
// L'Agente AI (property page) è ora l'unico entrypoint per interazione con l'AI.
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Onboarding from "@/pages/Onboarding";
import Notifications from "@/pages/Notifications";
import AdminAuditLog from "@/pages/AdminAuditLog";
import AdminFeedback from "@/pages/AdminFeedback";
import Playground from "@/pages/dev/Playground";
import SubscriptionOnboarding from "@/pages/SubscriptionOnboarding";
import GoogleCallback from "@/pages/GoogleCallback";
import Forbidden from "@/pages/Forbidden";
import { DemoActionProvider } from "@/components/DemoActionDialog";
import CookieBanner from "@/components/CookieBanner";
import PWAInstallBanner from "@/components/PWAInstallBanner";

import Landing from "@/pages/public/Landing";
import { BlogList, BlogDetail } from "@/pages/public/Blog";
// Iter207 — /chi-siamo è la nuova pagina "About" che ora include la mappa
// (prima esposta come /mappa). Lazy-load perché porta con sé leaflet
// + react-leaflet + markercluster (~250KB gzipped).
const ChiSiamo = lazy(() => import("@/pages/public/ChiSiamo"));
import Contatti from "@/pages/public/Contatti";
import ClientCheckout from "@/pages/public/ClientCheckout";
import ClientSchedule from "@/pages/public/ClientSchedule";
import ClientTracking from "@/pages/public/ClientTracking";
import PrivacyPolicy from "@/pages/public/PrivacyPolicy";
import RatePractice from "@/pages/public/RatePractice";
import SignDocument from "@/pages/public/SignDocument";
import PublicUploadRequest from "@/pages/public/PublicUploadRequest";
import PublicScheduleSecondary from "@/pages/public/PublicScheduleSecondary";
import PublicAppointmentPage from "@/pages/PublicAppointmentPage";
import TermsOfService from "@/pages/public/TermsOfService";
import PublicSite from "@/pages/public/PublicSite";

/**
 * ProtectedRoute — guard che protegge le rotte private + RBAC.
 *
 * @param {ReactNode} children       Contenuto da proteggere
 * @param {string[]}  allowedRoles   (opz.) elenco ruoli ammessi.
 *                                    Se omesso: qualsiasi utente autenticato.
 *                                    Ruoli riconosciuti:
 *                                      - "admin" (o `is_supervisor:true`)
 *                                      - "professional"
 *                                      - "operator" (macro-ruolo che include
 *                                        `site_operator`, `tech_operator`,
 *                                        `desk_operator`, `accounting_operator`)
 *                                      - ruoli operatori specifici (es. "site_operator")
 *
 * Redirect:
 *   - Non autenticato → /login?next=<originale>
 *   - pending_approval → /pending
 *   - needs_onboarding → /onboarding
 *   - Ruolo non autorizzato → /forbidden
 */
const OPERATOR_ROLES = ["site_operator", "tech_operator", "desk_operator", "accounting_operator"];

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="p-8">Caricamento...</div>;
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (user.status === "pending_approval") return <Navigate to="/pending" replace />;
  if (user.needs_onboarding && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }
  // Iter209 — Gate abbonamento: il Pro Master (professional senza
  // parent_user_id) deve scegliere un piano al primo accesso prima di
  // poter usare qualsiasi altra schermata.
  // Iter209ac — Escludi la sessione demo (`ht_demo_mode=1`) dal gate:
  // il demouser è un'esplorazione pubblica read-only e non deve mai
  // atterrare su `/subscription-onboarding` (che è riservato ai Pro
  // Master reali che non hanno ancora scelto un piano).
  const isDemoSession = (typeof window !== "undefined") &&
    window.localStorage &&
    window.localStorage.getItem("ht_demo_mode") === "1";
  const isProMasterUser = user.role === "professional" && !user.parent_user_id;
  if (
    isProMasterUser &&
    !isDemoSession &&
    !user.subscription_onboarding_completed &&
    location.pathname !== "/subscription-onboarding" &&
    location.pathname !== "/onboarding"
  ) {
    return <Navigate to="/subscription-onboarding" replace />;
  }
  // Iter209ac — Se un demouser tenta di aprire manualmente
  // `/subscription-onboarding` (per URL diretto o link stantio),
  // reindirizzalo alla dashboard: la pagina è per utenti reali.
  if (isDemoSession && location.pathname === "/subscription-onboarding") {
    return <Navigate to="/dashboard" replace />;
  }
  // RBAC check: se `allowedRoles` fornito, verifica appartenenza
  if (Array.isArray(allowedRoles) && allowedRoles.length > 0) {
    const isAdmin = user.role === "admin" || user.is_supervisor;
    const isPro = user.role === "professional";
    const isOperator = OPERATOR_ROLES.includes(user.role);
    const allowsAdmin = allowedRoles.includes("admin");
    const allowsPro = allowedRoles.includes("professional");
    const allowsOperator = allowedRoles.includes("operator");
    const allowsSpecificOperator = allowedRoles.includes(user.role);
    const authorized =
      (isAdmin && allowsAdmin) ||
      (isPro && allowsPro) ||
      (isOperator && (allowsOperator || allowsSpecificOperator));
    if (!authorized) {
      return <Navigate to="/forbidden" replace />;
    }
  }
  return children;
};

const OnboardingGuard = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8">Caricamento...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// Iter209al / Iter209ao — Gate feature "Email": consente l'accesso
// solo agli admin e agli operatori (chi può avere una casella
// @housetag.it associata). Pro Master e altri ruoli redirigono a
// `/dashboard`.
const EmailFeatureGuard = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8">Caricamento...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return children;
  if ((user.role || "").endsWith("_operator")) return children;
  return <Navigate to="/dashboard" replace />;
};

function AppShell() {
  return (
    <Routes>
      {/* ============================================================
          🌍 ROTTE PUBBLICHE — accessibili senza login
          ------------------------------------------------------------
          Landing, mappa pubblica, Termini di Servizio e Privacy Policy
          restano visibili a chiunque (marketing + obblighi legali).
          Tutto il resto è protetto da ProtectedRoute.
          ============================================================ */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Landing />} />
        <Route path="/chi-siamo" element={
          <Suspense fallback={<div className="max-w-7xl mx-auto px-6 py-16 text-center text-muted-foreground">Caricamento…</div>}>
            <ChiSiamo />
          </Suspense>
        } />
        {/* Iter207 — /mappa e /map ora reindirizzano alla sezione mappa dentro /chi-siamo */}
        <Route path="/mappa" element={<Navigate to="/chi-siamo#mappa" replace />} />
        <Route path="/map" element={<Navigate to="/chi-siamo#mappa" replace />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy/terms" element={<TermsOfService />} />
        {/* Iter200 — Blog pubblico: accessibile senza login. Endpoints
            `/api/public/blog` e `/api/public/blog/:slug` sono già open.
            L'editor Admin (post/patch/delete) resta protetto via
            `/admin/public-content` + `/api/admin/blog` (require_admin). */}
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:slug" element={<BlogDetail />} />
      </Route>

      {/* Flussi di autenticazione e transazionali (link email cliente)
          — rimangono pubblici per necessità funzionale. */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/google/callback" element={<GoogleCallback />} />
      <Route path="/pending" element={<Pending />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/view-file/:fileId" element={<ViewFile />} />

      {/* Iter209am — Accettazione invito operatore tramite link pubblico. */}
      <Route path="/join/:token" element={<JoinInvite />} />
      {/* Pagamento cliente finale via link email: deve funzionare senza login */}
      <Route path="/pay/:pid" element={<ClientCheckout />} />
      {/* Iter139 — Prenotazione sopralluogo post-bonifico (link inviato per email) */}
      <Route path="/schedule/:pid" element={<ClientSchedule />} />
      {/* Iter208.96 — Tracking pubblico pratica per cliente finale (link inviato per email) */}
      <Route path="/pratica/:token" element={<ClientTracking />} />
      {/* Rating pratica via link email al cliente */}
      <Route path="/rate/:pid/:score" element={<RatePractice />} />
      {/* Iter113 — Firma documento pubblica (link JWT nell'email) */}
      <Route path="/sign/:practice_id/:file_id" element={<SignDocument />} />
      <Route path="/upload-request/:token" element={<PublicUploadRequest />} />
      <Route path="/schedule-secondary/:pid/:token" element={<PublicScheduleSecondary />} />
      <Route path="/book-appointment/:token" element={<PublicAppointmentPage />} />
      {/* Iter208.76/77 — Sito pubblico del pro (Atrium / Casa Vicina) — router wrapper */}
      <Route path="/site/:slug/*" element={<PublicSite />} />
      {/* Onboarding: ha la sua guard interna (richiede user ma non è Layout) */}
      <Route path="/onboarding" element={<OnboardingGuard><Onboarding /></OnboardingGuard>} />
      {/* Iter209 — Selezione abbonamento al primo accesso Pro Master */}
      <Route
        path="/subscription-onboarding"
        element={
          <ProtectedRoute allowedRoles={["professional"]}>
            <SubscriptionOnboarding />
          </ProtectedRoute>
        }
      />

      {/* Pagina "accesso negato" — mostrata da ProtectedRoute con allowedRoles */}
      <Route path="/forbidden" element={<Forbidden />} />

      {/* ============================================================
          🔒 AREA PRIVATA — Professionista / Operatore / Admin
          ------------------------------------------------------------
          Ogni rotta ha `allowedRoles` esplicito per attuare RBAC.
          Ruoli riconosciuti: "admin", "professional", "operator"
          (macro che include tutti gli operatori specifici).
          ============================================================ */}
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        {/* Rotte condivise da tutti i ruoli autenticati */}
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/settings" element={<Settings />} />
        {/* Iter209af — Pagina Email; Iter209al — accessibile SOLO se
            il piano attivo dell'organizzazione include email (email/
            site/all). Con piano free/null redirect a /dashboard.
            Admin HouseTag sempre autorizzato. */}
        <Route path="/email" element={<EmailFeatureGuard><EmailPage /></EmailFeatureGuard>} />
        <Route path="/email/settings" element={<EmailFeatureGuard><EmailSettings /></EmailFeatureGuard>} />
        <Route path="/site-wizard" element={<ProtectedRoute roles={["professional"]}><SiteWizard /></ProtectedRoute>} />
        <Route path="/site-preview" element={<ProtectedRoute roles={["professional"]}><SitePreview /></ProtectedRoute>} />

        {/* Area PROFESSIONISTA — accessibile solo a pro + admin (che vede tutto) */}
        <Route path="/properties" element={
          <ProtectedRoute allowedRoles={["professional", "admin"]}><Properties /></ProtectedRoute>
        } />
        <Route path="/properties/:id" element={
          <ProtectedRoute allowedRoles={["professional", "admin"]}><PropertyDetail /></ProtectedRoute>
        } />
        <Route path="/clients" element={
          <ProtectedRoute allowedRoles={["professional"]}><Clients /></ProtectedRoute>
        } />
        <Route path="/plafond" element={
          <ProtectedRoute allowedRoles={["professional"]}><PlafondSummary /></ProtectedRoute>
        } />
        <Route path="/marketplace" element={
          <ProtectedRoute allowedRoles={["professional", "admin"]}><Marketplace /></ProtectedRoute>
        } />

        {/* Pratica: leggibile da PRO owner (backend gate su professional_id),
            operatori assegnati e admin. Il gate specifico è nel backend. */}
        <Route path="/practices/:id" element={<PracticeDetail />} />
        <Route path="/marketplace/practices/:id" element={<PracticeDetail />} />

        {/* Area OPERATORE — kanban board, calendario, compensi personali, form template */}
        <Route path="/board" element={
          <ProtectedRoute allowedRoles={["operator", "admin"]}><KanbanBoard /></ProtectedRoute>
        } />
        <Route path="/calendar" element={
          <ProtectedRoute allowedRoles={["operator", "admin"]}><CalendarPage /></ProtectedRoute>
        } />
        <Route path="/pro-calendar" element={
          <ProtectedRoute allowedRoles={["professional", "admin", "tech_operator", "desk_operator", "accounting_operator"]}><ProCalendarPage /></ProtectedRoute>
        } />
        <Route path="/my-compensations" element={
          <ProtectedRoute allowedRoles={["operator", "admin"]}><MyCompensations /></ProtectedRoute>
        } />

        {/* Area ADMIN — riservata solo agli admin/supervisor */}
        <Route path="/admin/marketplace" element={
          <ProtectedRoute allowedRoles={["admin"]}><AdminMarketplace /></ProtectedRoute>
        } />
        <Route path="/users" element={
          <ProtectedRoute allowedRoles={["admin"]}><Users /></ProtectedRoute>
        } />
        <Route path="/services-admin" element={
          <ProtectedRoute allowedRoles={["admin"]}><ServicesAdmin /></ProtectedRoute>
        } />
        <Route path="/accounting" element={
          <ProtectedRoute allowedRoles={["admin", "accounting_operator"]}><Accounting /></ProtectedRoute>
        } />
        {/* Iter142 — Redirect legacy: la pagina è ora una tab dentro /accounting */}
        <Route path="/accounting/awaiting-payments" element={<Navigate to="/accounting" replace />} />
        <Route path="/team" element={
          <ProtectedRoute allowedRoles={["professional", "admin"]}><TeamPage /></ProtectedRoute>
        } />
        <Route path="/admin/chatbot" element={
          <ProtectedRoute allowedRoles={["admin"]}><ChatbotHub /></ProtectedRoute>
        } />
        <Route path="/admin/chatbot-settings" element={
          <ProtectedRoute allowedRoles={["admin"]}><ChatbotSettings /></ProtectedRoute>
        } />
        <Route path="/admin/audit-log" element={
          <Navigate to="/settings?tab=audit-log" replace />
        } />
        <Route path="/admin/feedback" element={
          <ProtectedRoute allowedRoles={["admin"]}><AdminFeedback /></ProtectedRoute>
        } />
        <Route path="/my-domain" element={
          <ProtectedRoute allowedRoles={["professional"]}><MyDomain /></ProtectedRoute>
        } />
        <Route path="/dev/playground" element={
          <ProtectedRoute allowedRoles={["admin"]}><Playground /></ProtectedRoute>
        } />

        {/* Iter200 — Blog spostato in area pubblica (vedi PublicLayout sopra) */}
        <Route path="/contatti" element={<Contatti />} />
      </Route>

      {/* Fallback: qualunque URL sconosciuto → landing (che è pubblica). */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <NotificationsProvider>
          <DemoActionProvider>
            <AppShell />
            <Toaster richColors position="top-right" />
            <CookieBanner />
            <PWAInstallBanner />
          </DemoActionProvider>
        </NotificationsProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;

// Iter208.99 — /my-domain è stato spostato nella tab "Abbonamenti" di /settings.
// Preserviamo query params (es. `?resume=1` dall'email onboarding).
function MyDomainRedirect() {
  const [params] = useSearchParams();
  const qs = params.toString();
  const target = qs
    ? `/settings?tab=abbonamenti&${qs}`
    : "/settings?tab=abbonamenti";
  return <Navigate to={target} replace />;
}
